import React, { useEffect } from 'react';
import PageHeader from '@atlassian/jira-common-components-page-header';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import ReportErrors from '@atlassian/jira-errors-handling/src/utils/reporting-error-boundary.tsx';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import { PermissionGenericError } from '@atlassian/jira-standard-errors/src/common/utils/permission-generic-error/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { ARCHIVE_STATUS } from '../common/constants';
import TableController from '../controllers/table-controller';
import Filters from './filters';
import messages from './messages';
import ProjectsTable from './projects-table';
import type { Props } from './types';

const InactiveProjectsPage = (props: Props) => {
	const {
		intl: { formatMessage },
		status,
		ariaLabel,
	} = props;

	const isAdmin = useIsAdmin();

	useEffect(() => {
		if (!isAdmin) {
			const permissionError = new PermissionGenericError('User is not Jira Admin');
			fireErrorAnalytics({
				meta: {
					id: 'trashAndArchivePermissionError',
					packageName: 'jiraInactiveProjectsDirectoryBase',
				},
				error: permissionError,
				attributes: {
					status,
				},
			});
			throw permissionError;
		}
	}, [isAdmin, status]);

	const pageHeader =
		status === ARCHIVE_STATUS ? messages.headerCaptionForArchive : messages.headerCaptionForTrash;

	return (
		<ReportErrors id={`${status}Directory`} packageName="jiraInactiveProjectsDirectoryBase">
			<PageHeader truncateTitle>{formatMessage(pageHeader)}</PageHeader>
			<TableController status={status}>
				{({
					isLoading,
					data,
					page,
					limit,
					onPageChange,
					onSort,
					sort,
					refetchProjects,
					onFilterChange,
					filters,
				}) => (
					<>
						<Filters onFilterChange={onFilterChange} filters={filters} ariaLabelText={ariaLabel} />
						<ProjectsTable
							isLoading={isLoading}
							data={data}
							page={page}
							limit={limit}
							onPageChange={onPageChange}
							onSort={onSort}
							sortKey={sort.sortKey}
							sortOrder={sort.sortOrder}
							status={status}
							refetchProjects={refetchProjects}
						/>
					</>
				)}
			</TableController>
		</ReportErrors>
	);
};

InactiveProjectsPage.defaultProps = {
	status: ARCHIVE_STATUS,
};

export default injectIntl(InactiveProjectsPage);
